import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Alert } from '@material-ui/lab';
import { TextField, makeStyles } from '@material-ui/core';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { trim } from 'lodash';

import { ModalDialog } from '../Modal';
import { getBrand, createBrand, updateBrand } from '../../stores/dataorch';

const useStyles = makeStyles(() => ({
  uploadBtn: {
    display: 'block',
    color: '#96979a',
    fontSize: '12px',
    cursor: 'pointer',
    padding: '4px 6px',
    borderRadius: '4px',
    border: '1px solid #96979a',
    width: '45px',
    fontFamily: 'AvenirNext-DemiBold',
  },
  label: {
    textAlign: 'left',
    fontFamily: 'AvenirNext-DemiBold',
    fontSize: '13px',
    color: '#96979a',
    marginBottom: '4px',
    textTransform: 'uppercase',
  },
  row: {
    display: 'flex',
    marginBottom: '18px',
    gap: '10px',
    alignItems: 'center',
  },
  column: {
    width: '100%',
  },
  logo: {
    maxWidth: '60px',
    aspectRatio: 'auto',
    marginBottom: '10px',
  },
  error: {
    marginBottom: '4px',
  },
  required: {
    color: 'red',
    fontSize: '12px',
  },
  heading: {
    color: '#4a4a4a',
    margin: '0 0 10px 0',
    fontFamily: 'AvenirNext-DemiBold',
  },
  helperText: {
    margin: '4px 0 0 0',
  },
}));

const phoneRegExp = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im;

const schema = yup.object({
  name: yup
    .string()
    .trim()
    .required('Name is required'),
  code: yup
    .string()
    .trim()
    .required('Code is required'),
  phone_number: yup
    .string()
    .trim()
    .matches(phoneRegExp, { message: 'Invalid phone number format', excludeEmptyString: true }),
  contact_phone_number: yup
    .string()
    .trim()
    .matches(phoneRegExp, { message: 'Invalid phone number format', excludeEmptyString: true }),
  email: yup
    .string()
    .trim()
    .email('Invalid email format'),
  contact_email: yup
    .string()
    .trim()
    .email('Invalid email format'),
});

function BrandDetail({ selectedBrandId, onSuccess, onClose }) {
  const classes = useStyles();
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      id: '',
      name: '',
      email: '',
      phone_number: '',
      code: '',
      logo: '',
      contact_name: '',
      contact_email: '',
      contact_phone_number: '',
    },
    resolver: yupResolver(schema),
  });

  const [error, setError] = useState(null);
  const [logoError, setLogoError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [logo, setLogo] = useState(null);

  const handleUpload = (e) => {
    const logo = e.target.files[0];
    const types = ['image/png', 'image/jpeg'];
    if (!types.includes(logo.type)) return setLogoError('Invalid file type. Only accept JPEG/PNG image');

    setLogoError(null);
    setLogo(logo);
  };

  const onSubmit = (data) => {
    setError(null);

    const formData = new FormData();
    const ALLOW_SUBMIT_FIELDS = [
      'id',
      'name',
      'email',
      'phone_number',
      'code',
      'contact_name',
      'contact_email',
      'contact_phone_number',
      'is_use_profile_name',
      'is_use_profile_logo',
      'is_use_profile_label',
      'is_use_profile_email',
      'is_use_profile_phone',
    ];

    if (logo && typeof logo !== 'string') formData.append('logo', logo);

    const keys = Object.keys(data);
    for (const key of keys) {
      if (ALLOW_SUBMIT_FIELDS.indexOf(key) !== -1) {
        if (typeof data[key] === 'string') data[key] = trim(data[key]);
        formData.append(key, data[key]);
      }
    }

    const createOrUpdateAPI = data.id ? updateBrand : createBrand;
    createOrUpdateAPI(formData)
      .then((response) => {
        setSubmitting(false);

        if (response.status >= 200 && response.status < 300) {
          const message = data.id ? 'Brand was updated' : 'Brand was created';
          return onSuccess(message);
        }

        const message = response?.data?.message || 'Something went wrong. Please try again!';
        setError(message);
      })
      .catch((error) => {
        setSubmitting(false);
        setError(error?.message || error.toString());
      });
  };

  useEffect(() => {
    if (!selectedBrandId) return;

    getBrand(selectedBrandId)
      .then((response) => {
        const brand = response?.data?.client_profile;

        setValue('id', brand?.id || '');
        setValue('name', brand?.name || '');
        setValue('email', brand?.email || '');
        setValue('phone_number', brand?.phone_number || '');
        setValue('code', brand?.code || '');
        setValue('contact_name', brand?.contact_name || '');
        setValue('contact_email', brand?.contact_email || '');
        setValue('contact_phone_number', brand?.contact_phone_number || '');
        setLogo(brand?.logo || '');
      })
      .catch((error) => setError(error?.message || error.toString()));
  }, [selectedBrandId]);

  return (
    <ModalDialog title={selectedBrandId ? 'Edit Brand' : 'Add Brand'} open onOk={handleSubmit(onSubmit)} okLabel="SAVE" onClose={onClose} disabledOk={submitting || Boolean(logoError)} minWidth="700px">
      <form action="" onSubmit={handleSubmit(onSubmit)}>
        {error && (
          <Alert icon={false} severity="error" className={classes.error} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <h4 className={classes.heading}>Brand Contact</h4>
        <div className={classes.row}>
          <div className={classes.column}>
            <div className={classes.label}>
              Brand Name <span className={classes.required}>*</span>
            </div>
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  inputRef={field.ref}
                  type="text"
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={Boolean(errors?.name)}
                  helperText={errors?.name?.message}
                  FormHelperTextProps={{ classes: { root: classes.helperText } }}
                />
              )}
            />
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.column}>
            <div className={classes.label}>Brand Support Email</div>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="email"
                  inputRef={field.ref}
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={Boolean(errors?.email)}
                  helperText={errors?.email?.message}
                  FormHelperTextProps={{ classes: { root: classes.helperText } }}
                />
              )}
            />
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.column}>
            <div className={classes.label}>Brand Support Phone Number</div>
            <Controller
              name="phone_number"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="text"
                  inputRef={field.ref}
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={Boolean(errors?.phone_number)}
                  helperText={errors?.phone_number?.message}
                  FormHelperTextProps={{ classes: { root: classes.helperText } }}
                />
              )}
            />
          </div>
          <div className={classes.column}>
            <div className={classes.label}>
              Code <span className={classes.required}>*</span>
            </div>
            <Controller
              name="code"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="text"
                  inputRef={field.ref}
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={Boolean(errors?.code)}
                  helperText={errors?.code?.message}
                  FormHelperTextProps={{ classes: { root: classes.helperText } }}
                />
              )}
            />
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.column}>
            <div className={classes.label}>Brand Logo</div>
            {logo && <img className={classes.logo} src={typeof logo === 'string' ? logo : URL.createObjectURL(logo)} alt="" />}
            <input type="file" accept="image/png,image/jpeg" name="logo" id="logo" style={{ display: 'none' }} onChange={handleUpload} />
            <label htmlFor="logo" className={classes.uploadBtn}>
              + Photo
            </label>
            {logoError && <span className={classes.error}>{logoError}</span>}
          </div>
        </div>

        <h4 className={classes.heading}>Marketing Contact</h4>
        <div className={classes.row}>
          <div className={classes.column}>
            <div className={classes.label}>Contact Name</div>
            <Controller
              name="contact_name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="text"
                  inputRef={field.ref}
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={Boolean(errors?.contact_name)}
                  helperText={errors?.contact_name?.message}
                  FormHelperTextProps={{ classes: { root: classes.helperText } }}
                />
              )}
            />
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.column}>
            <div className={classes.label}>Email</div>
            <Controller
              name="contact_email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="email"
                  inputRef={field.ref}
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={Boolean(errors?.contact_email)}
                  helperText={errors?.contact_email?.message}
                  FormHelperTextProps={{ classes: { root: classes.helperText } }}
                />
              )}
            />
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.column}>
            <div className={classes.label}>Phone Number</div>
            <Controller
              name="contact_phone_number"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="text"
                  inputRef={field.ref}
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={Boolean(errors?.contact_phone_number)}
                  helperText={errors?.contact_phone_number?.message}
                  FormHelperTextProps={{ classes: { root: classes.helperText } }}
                />
              )}
            />
          </div>
        </div>
      </form>
    </ModalDialog>
  );
}

export default BrandDetail;
